import { useOptionalBridge } from '@web/adapter/useBridgeRuntime'
import { api } from '@web/api/client'
import { GitHubConnect } from '@web/components/GitHubConnect'
import {
  isDataObject,
  readableResult,
} from '@web/components/tools/StructuredContent'
import { useIdentity } from '@web/state/identity'
import { useEffect, useId, useState } from 'react'

type Connection = { id: string; title: string; connected: boolean }

/** Credentials are submitted directly to the host and never enter the conversation. */
function ConnectionForm({
  connection,
  onChange,
}: {
  connection: Connection
  onChange: () => void
}) {
  const inputId = useId()
  const [token, setToken] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  const submit = async (remove = false) => {
    setBusy(true)
    setError('')
    try {
      if (remove) await api.removeMcpConnection(connection.id)
      else await api.saveMcpConnection(connection.id, token)
      setToken('')
      setSaved(!remove)
      onChange()
    } catch (failure) {
      setError((failure as Error).message)
    } finally {
      setBusy(false)
    }
  }
  if (connection.id === 'github') {
    return (
      <section className="my-3" aria-label="GitHub connection">
        <p className="my-2 text-sm text-muted">
          Authorize your GitHub account to continue, then retry the request.
        </p>
        <GitHubConnect reconnect={connection.connected} />
      </section>
    )
  }
  return (
    <section className="my-3" aria-label={`${connection.title} connection`}>
      <h3 className="font-semibold">
        {connection.title} ·{' '}
        {connection.connected ? 'Connected' : 'Not connected'}
      </h3>
      {saved && (
        <p role="status" className="my-2 text-sm">
          Connected. Ask the agent to continue.
        </p>
      )}
      <p className="my-2 text-sm text-muted">
        Use a personal access token for the account this session should access.
        Your token is saved privately and is never sent as a chat message.
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault()
          void submit()
        }}
      >
        <label htmlFor={inputId}>{connection.title} access token</label>
        <input
          id={inputId}
          className="control my-2 w-full"
          type="password"
          autoComplete="off"
          required
          value={token}
          onChange={(event) => setToken(event.target.value)}
        />
        <button
          type="submit"
          className="btn btn-primary"
          disabled={busy || !token}
        >
          {busy
            ? 'Saving…'
            : connection.connected
              ? 'Replace connection'
              : `Connect ${connection.title}`}
        </button>
        {connection.connected && (
          <button
            type="button"
            className="btn ml-2"
            disabled={busy}
            onClick={() => void submit(true)}
          >
            Disconnect
          </button>
        )}
      </form>
      {error && (
        <p role="alert" className="mt-2 text-danger">
          {error}
        </p>
      )}
    </section>
  )
}

/** Recheck the current account and organization rather than trusting tool-provided URLs. */
export function McpConnections({
  serverId,
  includeGitHub = false,
}: {
  serverId?: string
  includeGitHub?: boolean
}) {
  const identity = useIdentity()
  const [connections, setConnections] = useState<Connection[] | null>(null)
  const [error, setError] = useState('')
  const [revision, setRevision] = useState(0)
  // Refresh on identity changes and successful saves, even though auth uses cookies.
  // biome-ignore lint/correctness/useExhaustiveDependencies: explicit authentication scope and refresh key
  useEffect(() => {
    let active = true
    setError('')
    void api
      .mcpConnections()
      .then((result) => {
        if (active) setConnections(result.connections)
      })
      .catch((failure: Error) => {
        if (active) setError(failure.message)
      })
    return () => {
      active = false
    }
  }, [
    identity.state?.current_user?.id,
    identity.state?.current_org?.id,
    revision,
  ])
  const shown = connections?.filter((item) =>
    serverId ? item.id === serverId : includeGitHub || item.id !== 'github',
  )
  return (
    <div>
      {connections === null && !error && (
        <p role="status">Loading connections…</p>
      )}
      {error && (
        <p role="alert" className="text-danger">
          {error}
        </p>
      )}
      {serverId && shown?.length === 0 && (
        <p>This server is no longer enabled for your organization.</p>
      )}
      {shown?.map((connection) => (
        <ConnectionForm
          key={`${identity.state?.current_user?.id}:${identity.state?.current_org?.id}:${connection.id}`}
          connection={connection}
          onChange={() => setRevision((value) => value + 1)}
        />
      ))}
    </div>
  )
}

/** Show the same connection flow for every trusted Canopy MCP authentication request. */
export function McpConnectionRequest({
  result,
  toolName,
}: {
  result: unknown
  toolName: string
}) {
  const bridge = useOptionalBridge()
  const identity = useIdentity()
  const [open, setOpen] = useState(false)
  const data = readableResult(result)
  const allowedNames = [
    'canopy.mcp_tools',
    'puck_canopy_mcp_tools_626f2f905f',
    'canopy_canopy.mcp_tools',
    'canopy.mcp_call',
    'puck_canopy_mcp_call_b01322d716',
    'canopy_canopy.mcp_call',
    'canopy_mcp_tools',
    'canopy_mcp_call',
  ]
  const name = toolName.startsWith('mcp__canopy__')
    ? toolName.slice('mcp__canopy__'.length)
    : toolName
  if (
    !allowedNames.includes(name) ||
    !isDataObject(data) ||
    data.status !== 'connection_required' ||
    !isDataObject(data.connection) ||
    typeof data.connection.id !== 'string'
  )
    return null
  const record = bridge?.snapshot.bridge.record
  if (
    !record ||
    record.owner_user_id !== identity.state?.current_user?.id ||
    record.org_id !== identity.state?.current_org?.id ||
    data.connection.org_id !== record.org_id
  )
    return null
  return (
    <section className="card my-3 p-4" aria-label="Connection required">
      <p className="font-semibold">
        Connect {data.connection.id === 'github' ? 'GitHub' : 'a service'} to
        continue
      </p>
      <p className="my-2 text-sm text-muted">
        This request needs access to your account.
      </p>
      {open ? (
        <McpConnections serverId={data.connection.id} />
      ) : (
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setOpen(true)}
        >
          Connect {data.connection.id === 'github' ? 'GitHub' : 'account'}
        </button>
      )}
    </section>
  )
}
